import { useState, useRef, useEffect, useCallback, useId } from 'react'
import Sentry from '@/lib/sentry-safe'
import { getAudioCoordinator } from '@/lib/audio-coordinator'
import { triggerHaptic } from '@/lib/haptics'
import {
  audioSrcTag,
  classifyPlayRejection,
  resolveMediaFailureKind,
  shouldReportAudioFailure,
} from '@/lib/audio-playback-telemetry'

export interface AudioBoundaries {
  start: number
  end: number
  loop?: boolean
}

export interface UseAudioPlayerProps {
  src?: string
  /**
   * The catalog row this source came from. Reported as a Sentry tag on asset
   * defects so a decode/format cluster reads as a list of rows to re-encode
   * instead of a list of URLs to map back by hand (TES-1874).
   */
  soundId?: string
  volume?: number
  preloadedElement?: HTMLAudioElement | null  // Optional pre-buffered element for instant playback
  boundaries?: AudioBoundaries  // Optional playback boundaries for trim preview
  onPlay?: () => void
  onPause?: () => void
  onEnded?: () => void
  onError?: (error: string) => void
  onBoundaryReached?: () => void  // Called when playback reaches end boundary
}

export interface UseAudioPlayerReturn {
  isPlaying: boolean
  isLoading: boolean
  currentTime: number
  duration: number
  progress: number
  error: string | null
  play: () => Promise<void>
  pause: () => void
  seek: (time: number) => Promise<void>
  toggle: () => Promise<void>
  setVolume: (volume: number) => void
  audioElement: HTMLAudioElement | null
}

export function useAudioPlayer({
  src,
  soundId,
  volume = 1,
  preloadedElement,
  boundaries,
  onPlay,
  onPause,
  onEnded,
  onError,
  onBoundaryReached
}: UseAudioPlayerProps): UseAudioPlayerReturn {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  // eslint-disable-next-line react-doctor/rendering-usetransition-loading -- loading state is driven by HTMLAudioElement events, not useTransition
  const [isLoading, setIsLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Mirrors audioRef.current into render-safe state, written only from
  // effects (never read during render off the ref itself) so consumers
  // (e.g. §11.3's useAudioLevel analyser) can react to the element existing.
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null)

  // Use a ref for boundaries to always have the latest values in event handlers
  // This prevents stale closure issues when boundaries change during playback.
  // Written in a commit-time effect (never during render) so it satisfies
  // react-hooks/refs; event handlers and the play/seek callbacks only read it
  // asynchronously (after effects have flushed), so the post-commit timing of
  // this write is correct.
  const boundariesRef = useRef(boundaries)
  useEffect(() => {
    boundariesRef.current = boundaries
  }, [boundaries])

  // Read only from the error reporters, which run long after this commits. A
  // ref rather than a dependency so naming the row can never re-run the src
  // effect and restart a load.
  const soundIdRef = useRef(soundId)
  useEffect(() => {
    soundIdRef.current = soundId
  }, [soundId])

  // Reset playback state when the source changes, using React's "adjust state
  // while rendering" pattern instead of a setState-in-effect. This applies the
  // reset before the browser paints the new src, avoiding a flash of stale
  // play/seek/error state and the cascading render an effect would cause.
  const [prevSrc, setPrevSrc] = useState(src)
  if (src !== prevSrc) {
    setPrevSrc(src)
    setIsPlaying(false)
    setCurrentTime(0)
    setError(null)
    if (!src) {
      // No source: also clear load/duration (mirrors the old else-branch).
      setIsLoading(false)
      setDuration(0)
    }
  }

  // Track if we're currently seeking to prevent race conditions with timeupdate
  const isSeekingRef = useRef(false)
  // Pending requestAnimationFrame id for the coalesced currentTime state
  // commit. timeupdate fires ~4-15×/s and each setCurrentTime fans out through
  // the audio-player context, re-rendering every visible SoundCard. We
  // coalesce to at most one React state commit per animation frame. Boundary
  // enforcement is NOT throttled — it reads audio.currentTime synchronously.
  const timeUpdateRafRef = useRef<number | null>(null)
  // Timer id for the boundary-loop seeking flag; cleared on cleanup to prevent
  // a stale timer from resetting isSeekingRef during a subsequent seek.
  const boundaryLoopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Generate a unique ID for this audio player instance
  const instanceId = useId()
  const coordinator = getAudioCoordinator()

  // Helper function for debug logging
  const debugLog = (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development') {
      const timestamp = new Date().toLocaleTimeString()
      const logMessage = `[${timestamp}] [useAudioPlayer]`
      if (data !== undefined) {
        console.log(`${logMessage} ${message}:`, data)
      } else {
        console.log(`${logMessage} ${message}`)
      }
    }
  }

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  // Subscribe to global audio coordinator to stop when another audio starts
  useEffect(() => {
    const unsubscribe = coordinator.subscribe(instanceId, (requesterId) => {
      debugLog(`Received stop request from ${requesterId}, pausing this player`)
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause()
        setIsPlaying(false)
      }
    })

    return () => {
      unsubscribe()
      coordinator.releasePlayback(instanceId)
    }
  }, [instanceId, coordinator])

  // Initialize audio element and set src - runs only when src changes.
  // State resets on src change are handled at render time above (not here),
  // so this effect only synchronizes the external <audio> element.
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- multiple setState calls here are in separate audio event handlers, not cascading from state reads
  useEffect(() => {
    debugLog('useEffect[src] triggered', { src, hasPreloaded: !!preloadedElement });
    if (src) {
      // Use preloaded element if provided (instant playback)
      // Note: preloadedElement.src is always absolute, src may be relative
      // So we compare by checking if preloaded src ends with the relative src
      let audio: HTMLAudioElement;
      let cancelled = false;
      const srcMatches = preloadedElement && (
        preloadedElement.src === src ||
        preloadedElement.src.endsWith(src) ||
        (src.startsWith('/') && preloadedElement.src.includes(src))
      );
      if (preloadedElement && srcMatches) {
        debugLog('Using preloaded audio element - instant playback!');
        audio = preloadedElement;
        // A preloaded element may be fully buffered (its load events already
        // fired) or still mid-download (they are yet to fire, and the
        // listeners below will catch them). Sync whatever it has into React
        // state, deferred out of the synchronous effect body (microtask) so it
        // doesn't cascade-render — it still lands before paint. Guarded so a
        // torn-down effect is a no-op.
        const preloaded = audio;
        queueMicrotask(() => {
          if (cancelled) return;
          setIsLoading(preloaded.readyState < 2);
          setDuration(preloaded.duration || 0);
        });
      } else {
        debugLog('Initializing audio with new src', src);
        // Construct without a src first so crossOrigin is set before the
        // element starts fetching — setting it after `new Audio(src)` would
        // have already kicked off a same-mode load and re-set it once more.
        audio = new Audio();
        // §11.3's useAudioLevel reads decoded samples through a
        // MediaElementAudioSourceNode + AnalyserNode. Without an explicit
        // CORS mode, a cross-origin element (every Supabase Storage URL is
        // cross-origin from teslalocksound.com) is "tainted": it plays fine,
        // but the AnalyserNode is spec-required to output silence forever —
        // this was Rule 3's bug (level stuck at 0, no error thrown).
        // Supabase Storage serves `access-control-allow-origin: *`
        // (lib/api/storage.ts) and /api/audio-proxy sets matching
        // Access-Control-Allow-Origin for the caller's own origin, so
        // 'anonymous' mode is satisfied by every URL storageApi.getAudioUrl
        // can return — this does not change playback or introduce load
        // failures for either source.
        audio.crossOrigin = 'anonymous';
        audio.preload = 'auto'; // Changed from 'metadata' to 'auto' for faster playback
        audio.src = src;
      }
      audioRef.current = audio;
      queueMicrotask(() => {
        if (cancelled) return;
        setAudioElement(audio);
      });

      const handleLoadStart = () => {
        debugLog('Audio load started');
        setIsLoading(true);
        setError(null);
      };

      const handleCanPlay = () => {
        debugLog('Audio can play');
        setIsLoading(false);
      };

      const handleLoadedMetadata = () => {
        debugLog('Audio metadata loaded', { duration: audio.duration });
        setDuration(audio.duration || 0);
      };

      const handleLoadedData = () => {
        debugLog('Audio data loaded');
        setIsLoading(false);
      };

      // Note: 'ended' event is handled in the callbacks useEffect to avoid duplicate listeners

      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('loadeddata', handleLoadedData);

      return () => {
        debugLog('Cleaning up audio element for src change');
        cancelled = true;
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('loadeddata', handleLoadedData);
        // Only pause and clear if not a preloaded element (we don't own it)
        if (!preloadedElement || audio !== preloadedElement) {
          audio.pause();
        }
        audioRef.current = null;
        setAudioElement(null);
      };
    } else {
      // No source: tear down the element. The corresponding state reset
      // (isPlaying/isLoading/currentTime/duration/error) happens at render
      // time above when `src` transitions to a falsy value.
      debugLog('No src provided, cleaning up');
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setAudioElement(null);
      }
    }
  }, [src, preloadedElement]);

  // Handle event listeners and state updates - runs when audioRef is set or callbacks change
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setState calls are in separate audio event handlers (play, pause, error, ended), not cascading
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    debugLog('useEffect[audioRef, callbacks] triggered');

    // Cancel any pending rAF-coalesced currentTime commit so it can't fire
    // after pause/end/unmount and overwrite a deliberate state write.
    const cancelPendingTimeCommit = () => {
      if (timeUpdateRafRef.current !== null) {
        cancelAnimationFrame(timeUpdateRafRef.current);
        timeUpdateRafRef.current = null;
      }
    };

    const handleTimeUpdate = () => {
      // Throttle ONLY the React state commit: coalesce timeupdate ticks so at
      // most one setCurrentTime lands per animation frame. The callback reads
      // audio.currentTime at frame time, so it always commits the freshest
      // position.
      if (timeUpdateRafRef.current === null) {
        timeUpdateRafRef.current = requestAnimationFrame(() => {
          timeUpdateRafRef.current = null;
          setCurrentTime(audio.currentTime);
        });
      }

      // Skip boundary checks if we're in the middle of seeking
      if (isSeekingRef.current) {
        return;
      }

      // Handle boundary enforcement - use ref to always have latest boundaries
      const currentBoundaries = boundariesRef.current;
      if (currentBoundaries && audio.currentTime >= currentBoundaries.end) {
        debugLog('Boundary reached', { currentTime: audio.currentTime, boundaryEnd: currentBoundaries.end });
        onBoundaryReached?.();

        if (currentBoundaries.loop) {
          debugLog('Looping to boundary start', currentBoundaries.start);
          isSeekingRef.current = true;
          audio.currentTime = currentBoundaries.start;
          // Clear seeking flag after a short delay to allow the seek to complete.
          // Cancel any pending timer first so a rapid second loop doesn't stomp
          // isSeekingRef for the new seek.
          if (boundaryLoopTimerRef.current) clearTimeout(boundaryLoopTimerRef.current);
          boundaryLoopTimerRef.current = setTimeout(() => {
            boundaryLoopTimerRef.current = null;
            isSeekingRef.current = false;
          }, 50);
        } else {
          debugLog('Pausing at boundary end');
          audio.pause();
          setIsPlaying(false);
        }
      }
    };
    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
      triggerHaptic('play-start'); // §12.8
      onPlay?.();
    };
    const handlePause = () => {
      // Drop any in-flight coalesced commit and settle state at the exact
      // paused position synchronously.
      cancelPendingTimeCommit();
      setCurrentTime(audio.currentTime);
      setIsPlaying(false);
      onPause?.();
    };
    const handleError = (event: Event) => {
      const target = event.target as HTMLAudioElement;
      let errorMessage = 'Failed to load audio file';
      let errorDetails = '';
      
      if (target.error) {
        switch (target.error.code) {
          case target.error.MEDIA_ERR_ABORTED: 
            errorMessage = 'Audio loading aborted';
            errorDetails = 'The audio loading was aborted by the user or browser.';
            break;
          case target.error.MEDIA_ERR_NETWORK: 
            errorMessage = 'Network error while loading audio';
            errorDetails = 'Check your internet connection or the audio file may be unavailable.';
            break;
          case target.error.MEDIA_ERR_DECODE: 
            errorMessage = 'Audio decoding error';
            errorDetails = 'The audio file appears to be corrupted or in an unsupported format.';
            break;
          case target.error.MEDIA_ERR_SRC_NOT_SUPPORTED: 
            errorMessage = 'Audio format not supported';
            errorDetails = 'This audio format is not supported by your browser. Try a different browser or convert the file to MP3/WAV format.';
            break;
          default: 
            errorMessage = `Audio error: ${target.error.message || 'Unknown error'}`;
            errorDetails = 'An unexpected error occurred while loading the audio.';
        }
        
        // Report only what is actionable, and only once per asset. A network
        // stall or an aborted load recurs on every play for an affected
        // visitor; a decode failure is one broken file, however many people
        // hit it. See lib/audio-playback-telemetry.ts (TES-1977).
        //
        // Snapshot before awaiting: the element is reused across source
        // switches, so `target.*` can describe a different load by the time the
        // reachability probe settles.
        const code = target.error.code;
        const message = target.error.message;
        const src = target.src;
        const networkState = target.networkState;
        const readyState = target.readyState;
        const soundId = soundIdRef.current ?? '<unknown>';
        const details = errorDetails;

        resolveMediaFailureKind(code, src)
          .then((kind) => {
            if (!shouldReportAudioFailure(kind, src)) return;
            Sentry.captureMessage('Media error in useAudioPlayer', {
              level: 'warning',
              tags: {
                context: 'useAudioPlayer',
                action: 'handleError',
                failureKind: kind,
                sound_id: soundId,
                audio_path: audioSrcTag(src),
              },
              extra: {
                code,
                message,
                src,
                networkState,
                readyState,
                errorDetails: details
              }
            });
          })
          // A rejection here would surface as an unhandled rejection, which the
          // SDK captures — a telemetry failure billing a telemetry event is the
          // feedback loop this module exists to prevent.
          .catch(() => {});
      }
      
      // Try to extract file extension from URL for better error messages
      try {
        const url = new URL(target.src);
        const pathname = url.pathname;
        const extension = pathname.split('.').pop()?.toLowerCase();
        if (extension) {
          errorMessage += ` (.${extension} file)`;
        }
      } catch (e) {
        // Ignore URL parsing errors
      }
      
      setError(errorMessage);
      setIsLoading(false);
      setIsPlaying(false);
      onError?.(errorMessage);
    };
    
    const handleEnded = () => {
      onEnded?.();
      // Cancel any pending coalesced commit so it can't fire after this and
      // overwrite the reset with the final playback position.
      cancelPendingTimeCommit();
      setIsPlaying(false); // Make sure isPlaying is reset
      setCurrentTime(0); // Reset currentTime on end
    };

    // Clear seeking flag when browser confirms seek is complete
    const handleSeeked = () => {
      debugLog('Seeked event fired');
      isSeekingRef.current = false;
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('seeked', handleSeeked);

    return () => {
      debugLog('Cleaning up event listeners');
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('seeked', handleSeeked);
      // Cancel any pending rAF-coalesced currentTime commit (unmount or src
      // change) so a stale frame callback can't commit into the next source's
      // state.
      cancelPendingTimeCommit();
      // Cancel any pending boundary-loop flag timer so it doesn't stomp
      // isSeekingRef after unmount or src change.
      if (boundaryLoopTimerRef.current) {
        clearTimeout(boundaryLoopTimerRef.current);
        boundaryLoopTimerRef.current = null;
      }
    };

  }, [src, onPlay, onPause, onEnded, onError, onBoundaryReached]); // boundaries handled via boundariesRef to avoid stale closures

  // Update volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      debugLog('useEffect[volume] triggered', { volume });
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const play = useCallback(async () => {
    const currentBoundaries = boundariesRef.current;
    debugLog('Play function called', {
      hasAudioRef: !!audioRef.current,
      paused: audioRef.current?.paused,
      currentSrc: audioRef.current?.src,
      readyState: audioRef.current?.readyState,
      boundaries: currentBoundaries
    })

    // Always attempt to play if we have an audio element
    // Don't check paused state - let the browser handle it
    // This ensures play() works even after boundary changes or seek operations
    if (audioRef.current) {
      // Request exclusive playback - this will stop all other audio players
      coordinator.requestPlayback(instanceId)
      debugLog('Requested exclusive playback from coordinator')

      // If boundaries are set, ensure we start within them
      // IMPORTANT: We must wait for the seek to complete before playing
      if (currentBoundaries) {
        const currentPos = audioRef.current.currentTime
        if (currentPos < currentBoundaries.start || currentPos >= currentBoundaries.end) {
          debugLog('Seeking to boundary start before play', currentBoundaries.start)
          isSeekingRef.current = true

          // Wait for the seek to complete before playing
          await new Promise<void>((resolve) => {
            const audio = audioRef.current
            if (!audio) { isSeekingRef.current = false; resolve(); return }
            let settled = false
            let fallback: ReturnType<typeof setTimeout>
            const finish = () => {
              if (settled) return
              settled = true
              clearTimeout(fallback)
              audio.removeEventListener('seeked', onSeeked)
              isSeekingRef.current = false
              resolve()
            }
            const onSeeked = () => { debugLog('Seek completed, ready to play'); finish() }
            audio.addEventListener('seeked', onSeeked)
            audio.currentTime = currentBoundaries.start
            fallback = setTimeout(finish, 100)
          })
        }
      }

      if (!audioRef.current) {
        debugLog('Audio element torn down during boundary seek; aborting play')
        return
      }

      debugLog('Attempting to play audio')
      debugLog('Audio element state', {
        readyState: audioRef.current.readyState,
        networkState: audioRef.current.networkState,
        src: audioRef.current.src,
        currentTime: audioRef.current.currentTime,
        duration: audioRef.current.duration
      })
      
      setError(null)
      
      try {
        // Ensure the audio is loaded before playing
        if (audioRef.current.readyState < 2) {
          debugLog('Audio not ready, waiting for loadeddata event...')
          await new Promise<void>((resolve, reject) => {
            const audio = audioRef.current
            if (!audio) { reject(new Error('Audio element torn down before load')); return }
            const timeout = setTimeout(() => {
              debugLog('Audio loading timeout after 10 seconds')
              audio.removeEventListener('loadeddata', onLoadedData)
              audio.removeEventListener('error', onError)
              reject(new Error('Audio loading timeout'))
            }, 10000)
            
            const onLoadedData = () => {
              debugLog('Audio data loaded, ready to play')
              clearTimeout(timeout)
              audio.removeEventListener('loadeddata', onLoadedData)
              audio.removeEventListener('error', onError)
              resolve()
            }
            
            const onError = () => {
              debugLog('Audio error while waiting to load')
              clearTimeout(timeout)
              audio.removeEventListener('loadeddata', onLoadedData)
              audio.removeEventListener('error', onError)
              reject(new Error('Failed to load audio'))
            }
            
            if (audio.readyState >= 2) {
              debugLog('Audio already loaded')
              clearTimeout(timeout)
              resolve()
            } else {
              audio.addEventListener('loadeddata', onLoadedData)
              audio.addEventListener('error', onError)
            }
          })
        }
        
        const audioEl = audioRef.current
        if (!audioEl) {
          debugLog('Audio element torn down while waiting for load; aborting play')
          return
        }
        debugLog('Calling audio.play()...')
        await audioEl.play()
        debugLog('Audio play() succeeded')
      } catch (err) {
        const error = err as Error
        let errorMessage = `Failed to play audio: ${error.message}`
        let isFatalError = true

        // Handle different types of errors
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Browser blocked audio playback. Click the play button to enable audio.'
          debugLog('NotAllowedError: Browser autoplay policy blocked audio')
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'Audio format not supported by your browser'
          debugLog('NotSupportedError: Audio format not supported')
        } else if (error.name === 'AbortError') {
          // AbortError is expected when switching audio sources quickly - not a real error
          debugLog('AbortError: Audio playback was aborted (expected when switching sources)')
          isFatalError = false
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Audio took too long to load. Please try again.'
          debugLog('Timeout error: Audio loading timeout')
        } else {
          debugLog('Unknown error', { name: error.name, message: error.message, stack: error.stack })
        }

        // Only set error state and call onError for fatal errors
        // AbortError is expected when switching sources and should not be reported
        if (isFatalError) {
          setError(errorMessage)
          onError?.(errorMessage)

          // A rejected play() never fires the element's `error` event, so this
          // is the only place these surface. Same rule as the handler above:
          // blocked autoplay and load timeouts are session conditions, not
          // defects, and an unsupported source reports once per asset.
          const kind = classifyPlayRejection(error)
          if (shouldReportAudioFailure(kind, audioRef.current?.src)) {
            Sentry.captureMessage('Audio play() rejected', {
              level: 'warning',
              tags: {
                context: 'useAudioPlayer',
                action: 'play',
                failureKind: kind,
                sound_id: soundIdRef.current ?? '<unknown>',
                audio_path: audioSrcTag(audioRef.current?.src),
              },
              extra: {
                name: error.name,
                message: error.message,
                src: audioRef.current?.src,
              },
            })
          }
        }
        setIsPlaying(false)
      }
    } else {
      debugLog('Cannot play audio - no audio element')
    }
  }, [onError, coordinator, instanceId])  // boundaries handled via boundariesRef

  const pause = useCallback(() => {
    debugLog('Pause function called', {
      hasAudioRef: !!audioRef.current,
      paused: audioRef.current?.paused
    })

    // Check audio element's actual state, not React state (avoids race conditions)
    if (audioRef.current && !audioRef.current.paused) {
      debugLog('Pausing audio')
      audioRef.current.pause()
      // Release playback so other players know we're not active
      coordinator.releasePlayback(instanceId)
    }
  }, [coordinator, instanceId])  // Removed isPlaying - we check audio.paused directly

  const seek = useCallback((time: number): Promise<void> => {
    const currentBoundaries = boundariesRef.current;
    debugLog('Seek function called', { time, duration, boundaries: currentBoundaries })

    if (audioRef.current) {
      let seekTime = Math.max(0, Math.min(time, duration || Infinity))

      // Constrain seeking to boundaries if set
      if (currentBoundaries) {
        seekTime = Math.max(currentBoundaries.start, Math.min(seekTime, currentBoundaries.end))
        debugLog('Constrained seek to boundaries', { original: time, constrained: seekTime })
      }

      debugLog('Seeking to time', seekTime)
      isSeekingRef.current = true

      // Return a promise that resolves when seek completes
      return new Promise<void>((resolve) => {
        const audio = audioRef.current
        if (!audio) { isSeekingRef.current = false; resolve(); return }
        let settled = false
        let fallback: ReturnType<typeof setTimeout>
        const finish = () => {
          if (settled) return
          settled = true
          clearTimeout(fallback)
          audio.removeEventListener('seeked', onSeeked)
          isSeekingRef.current = false
          resolve()
        }
        const onSeeked = () => { debugLog('Seek completed'); finish() }
        audio.addEventListener('seeked', onSeeked)
        audio.currentTime = seekTime
        fallback = setTimeout(finish, 100)
      })
    }

    return Promise.resolve()
  }, [duration])

  const toggle = useCallback(async () => {
    debugLog('Toggle function called', { currentState: isPlaying ? 'playing' : 'paused' })

    if (isPlaying) {
      pause()
    } else {
      await play()
    }
  }, [isPlaying, play, pause])

  const setVolume = useCallback((newVolume: number) => {
    const vol = Math.max(0, Math.min(1, newVolume))
    debugLog('SetVolume function called', { newVolume, clampedVolume: vol })

    if (audioRef.current) {
      audioRef.current.volume = vol
      debugLog('Volume set on audio element', vol)
    }
  }, [])

  // Log state changes in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugLog('State changed', {
        isPlaying,
        isLoading,
        currentTime: Math.round(currentTime * 10) / 10, // Round to 1 decimal
        duration: Math.round(duration * 10) / 10,
        progress: Math.round(progress * 10) / 10,
        hasError: !!error,
        errorMessage: error
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-doctor/exhaustive-deps -- only log on key state changes, not every time update
  }, [isPlaying, isLoading, error])

  return {
    isPlaying,
    isLoading,
    currentTime,
    duration,
    progress,
    error,
    play,
    pause,
    seek,
    toggle,
    setVolume,
    // Exposed for §11.3's envelope-follower analysis (useAudioLevel) — the
    // signal-in-type mask reads live amplitude from the same element that's
    // already playing, rather than opening a second audio pipeline.
    audioElement
  }
}